import { TEAM_PRESENCE_CLIENT_XPRO4 } from "./teamPresenceTelemetry";

/**
 * Linha do snapshot/rede é Xpro 4 (campo `client` ou telemetria típica do front 4).
 * Exportada para o normalizador de socket enriquecer linhas mínimas do servidor.
 * @param {object} row
 */
export function presenceRowIsXpro4(row) {
  const c = row?.client ?? row?.app;
  if (c === TEAM_PRESENCE_CLIENT_XPRO4 || c === "xpro4") return true;
  const hasTab = row?.tab != null && String(row.tab).trim() !== "";
  const tabStr = hasTab ? String(row.tab) : "";
  const tabLooksLikeXpro4Instance = tabStr.length >= 32 && tabStr.includes("-");
  const hasTelemetry =
    (row?.path != null && String(row.path) !== "") ||
    row?.since != null ||
    row?.lastActivityAt != null ||
    row?.focused === true ||
    row?.focused === false;
  if (hasTab && (hasTelemetry || tabLooksLikeXpro4Instance)) return true;
  return false;
}

/**
 * @typedef {{ path: string, since: string|null, lastActivityAt: string|null, focused: boolean, tabKey: string }} TeamPresenceXpro4Tab
 * @typedef {{ legacyTabCount: number, xpro4Tabs: TeamPresenceXpro4Tab[] }} TeamPresenceUserDetail
 */

/**
 * Agrega o snapshot bruto do evento socket `users` (uma linha por aba).
 * Legado = tudo que não é Xpro 4 (comportamento anterior ao cockpit; sem exigir `client` no legado).
 * @param {Array} data
 * @returns {{ counts: Map<number, number>, detail: Map<number, TeamPresenceUserDetail>, names: Map<number, string> }}
 */
export function buildPresenceFromUsersSnapshot(data) {
  /** @type {Map<number, number>} */
  const counts = new Map();
  /** @type {Map<number, { legacy: number, x4: Map<string, TeamPresenceXpro4Tab> }>} */
  const acc = new Map();
  /** @type {Map<number, string>} */
  const names = new Map();

  for (const row of data) {
    const uid = Number(row?.id);
    if (!Number.isFinite(uid)) continue;
    counts.set(uid, (counts.get(uid) || 0) + 1);
    const nm = row?.name != null ? String(row.name).trim() : "";
    if (nm && !names.has(uid)) names.set(uid, nm);

    if (!acc.has(uid)) acc.set(uid, { legacy: 0, x4: new Map() });
    const slot = acc.get(uid);

    if (presenceRowIsXpro4(row)) {
      const sid = row?.socketId != null ? String(row.socketId).trim() : "";
      const tabKey =
        row.tab != null
          ? sid
            ? `${String(row.tab)}::${sid}`
            : String(row.tab)
          : sid
            ? `path:${String(row.path || "")}::${sid}`
            : `path:${String(row.path || "")}`;
      slot.x4.set(tabKey, {
        path: String(row.path || ""),
        since: row.since != null ? String(row.since) : null,
        lastActivityAt: row.lastActivityAt != null ? String(row.lastActivityAt) : null,
        focused: Boolean(row.focused),
        tabKey,
      });
    } else {
      slot.legacy += 1;
    }
  }

  /** @type {Map<number, TeamPresenceUserDetail>} */
  const detail = new Map();
  for (const [uid, slot] of acc) {
    const xpro4Tabs = [...slot.x4.values()];
    detail.set(uid, {
      legacyTabCount: slot.legacy,
      xpro4Tabs,
    });
  }

  return { counts, detail, names };
}
